// Example File: packages/builder/src/pages/BuilderPage.tsx

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next'; // Example for i18n

// --- Imports from the Chatbot package ---
import { 
    createFrontendActions,
    interactiveToolRegistry,
    type FrontendToolContext
} from '@dopomogai/chat-react';

// --- Import the wrapper component ---
import { BuilderAgentChat } from './BuilderAgentChat'; // Assuming this path

export function BuilderPage() {
    const { t } = useTranslation();
    
    // This is where the actual state of the builder page lives.
    const [agentTitle, setAgentTitle] = useState(t('builder.defaultTitle', 'Untitled Agent'));
    const [selectedFeatures, setSelectedFeatures] = useState<string[]>([]);

    // --- 1. Correctly and fully implement the FrontendToolContext for THIS page ---
    const builderToolContext: FrontendToolContext = {
        selectPreset: (id) => {
            console.log(`[BuilderPage Context] Applying preset: ${id}`);
            // e.g., set a predefined list of features
            if (id === 'commerce_starter') {
                setSelectedFeatures(['inventory', 'payment_gateway', 'shipping']);
            }
        },
        setFeature: (id, enabled) => {
            console.log(`[BuilderPage Context] Setting feature ${id} to ${enabled}`);
            setSelectedFeatures(prev => 
                enabled ? [...new Set([...prev, id])] : prev.filter(f => f !== id)
            );
        },
        setPersonalizedCopy: (field, value) => {
            if (field === 'heroTitle') {
                console.log(`[BuilderPage Context] Setting agent title to: ${value}`);
                setAgentTitle(value);
            }
        },
        // --- THIS IS THE FIX ---
        // Must return a Promise<{ url } | { error, code? }>
        openQuoteWithCurrentConfig: async () => {
            console.log('[BuilderPage Context] Generating quote URL with current config.');
            const url = `/quote?title=${encodeURIComponent(agentTitle)}&features=${selectedFeatures.join(',')}`;
            // This could also trigger a navigation action, e.g., router.push(url);
            alert(`Quote would be generated at: ${url}`);
            return { url };
        },
        // Required by FrontendToolContext
        submitLeadEmail: async (email: string) => {
            console.log(`[BuilderPage Context] Submitting lead email: ${email}`);
            // Simulate successful submission
            return { ok: true, lead_id: 'demo-lead-id' };
        },
        // --- Provide no-op implementations for unused but required functions ---
        openSection: (id) => { 
            console.warn(`[BuilderPage Context] 'openSection' action called with ID '${id}', but is not implemented on this page.`);
        },
        safeNavigate: (path) => { 
            console.warn(`[BuilderPage Context] 'safeNavigate' action called with path '${path}', but is not implemented on this page.`);
            // A real implementation would use a router and check against a whitelist.
        },
        fillFormField: (formId, fieldId, value) => {
             console.warn(`[BuilderPage Context] 'fillFormField' action called for form '${formId}', but is not implemented on this page.`);
        },
    };

    // --- 2. Define the headless tools for this page ---
    const headlessTools = createFrontendActions(builderToolContext);

    return (
        <div className="builder-layout" style={{ display: 'flex', height: '100vh' }}>
            <main className="builder-canvas" style={{ flex: 1, padding: '2rem' }}>
                <h1 style={{ fontSize: '2rem', fontWeight: 'bold' }}>{agentTitle}</h1>
                <p><strong>Features:</strong> {selectedFeatures.join(', ') || 'None'}</p>
                {/* ... all your main builder UI components ... */}
            </main>
            <aside className="builder-sidebar" style={{ width: '400px', borderLeft: '1px solid #ccc', padding: '1rem' }}>
                <h2>Builder Controls</h2>
                {/* ... your builder controls ... */}
                <div style={{ marginTop: '2rem' }}>
                    <BuilderAgentChat
                        toolRegistry={interactiveToolRegistry}
                        headlessTools={headlessTools}
                        headlessContext={builderToolContext}
                    />
                </div>
            </aside>
        </div>
    );
}